import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { sugerirProvincias } from './provincias-sugeridas';

/**
 * Sugerencia de `GET /localidades?q=`. Espeja la interfaz `Localidad` del
 * backoffice (`src/services/localidades.service.ts`); acá sólo se usan
 * `nombre` y `detalle`, que son las dos líneas del desplegable.
 *
 * - `value`: slug canónico.
 * - `label`: la sugerencia completa ("San Fernando, Buenos Aires").
 * - `nombre`: sólo el nombre ("San Fernando").
 * - `detalle`: el contexto ("Cdad. Autónoma de Buenos Aires"); vacío cuando el
 *   catálogo es estático.
 * - `provincia`: slug de provincia, o `null`.
 */
export interface Localidad {
  value: string;
  label: string;
  nombre: string;
  detalle: string;
  provincia: string | null;
}

/**
 * Autocompletado de localidades del buscador público (FP-16).
 *
 * El endpoint **no** requiere sesión, que es lo que lo hace usable acá: el
 * portal es anónimo. Sólo se porta `buscar()`; `listar()` y `labels()` del
 * backoffice alimentan selects que el público no tiene.
 */
@Injectable({ providedIn: 'root' })
export class LocalidadesService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/localidades`;

  /**
   * Sugerencias mientras el usuario escribe. Ante error degrada a lista vacía:
   * el desplegable queda sin sugerencias pero el campo sigue aceptando texto
   * libre, que el backend igual resuelve por coincidencia parcial.
   *
   * Con `busquedaPorProvinciaHabilitada`, adelante van las provincias que
   * coinciden ("Provincia de Córdoba"), armadas acá y no en el backend: ver
   * `provincias-sugeridas.ts`. Siguen apareciendo aunque falle la consulta.
   */
  buscar(q: string): Observable<Localidad[]> {
    const provincias = environment.busquedaPorProvinciaHabilitada
      ? sugerirProvincias(q)
      : [];
    return this.http
      .get<Localidad[]>(this.base, { params: new HttpParams().set('q', q) })
      .pipe(
        catchError(() => of<Localidad[]>([])),
        map((localidades) => [...provincias, ...localidades]),
      );
  }
}
